"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Flame, Compass, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityCycles = [{
  period: "Oct – Mar",
  phase: "Peak MICE & Corporate Season",
  description: "Driven by international trade expos, Vibrant Gujarat summits, NRI winter visits, and major destination weddings. Premium ADR yield and minimum-stay fences are critical.",
  badgeColor: "text-secondary"
}, {
  period: "Apr – Jun",
  phase: "Summer Commercial Tactical Yield",
  description: "Extreme weather softens leisure traffic. Focus pivots toward corporate contracts, SME transit stays, local banquet events, and high-conversion OTA value bundling.",
  badgeColor: "text-chart-3"
}, {
  period: "Jul – Sep",
  phase: "Monsoon & Festive Buildup",
  description: "Rebound in manufacturing corridor transit, medical tourism, and early festive booking windows (Navratri preparation). Rate parity fencing shields margins.",
  badgeColor: "text-chart-2"
}];
const challenges = [{
  id: 1,
  title: "MICE & Exhibition Rate Compression",
  subtitle: "Demand Surge Management",
  iconName: "CalendarRange",
  description: "Ahmedabad witnesses intense demand spikes during mega events at Mahatma Mandir, Helipad Exhibition Centre, and Narendra Modi Stadium. We implement pace-based surge pricing and length-of-stay fences to capture maximum RevPAR without leaving shoulder nights vacant.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based dynamic pricing & minimum stay restrictions"
}, {
  id: 2,
  title: "Corporate vs. Weekend Leisure Balancing",
  subtitle: "Segment Yield Optimization",
  iconName: "LineChart",
  description: "Weekday occupancies are anchored by industrial hubs (Sanand, Changodar, SG Highway), while weekends require targeted leisure and staycation packages. We build segmented rate plans that sustain steady high-yield occupancy all 7 days a week.",
  metricLabel: "Strategy focus",
  metricValue: "Dynamic weekday-weekend yield fences & corporate renegotiation"
}, {
  id: 3,
  title: "OTA Dependency & Rate Undercutting",
  subtitle: "Distribution & Channel Parity",
  iconName: "ShieldAlert",
  description: "Mitigate heavy commission drains from OTAs like MakeMyTrip and Booking.com. We eliminate unapproved B2B wholesaler leakage, maintain iron-clad metasearch parity, and implement targeted direct booking incentives for repeating business travelers.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel conversion boost"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapeahmedabad" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-18">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Ahmedabad
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Ahmedabad combines a powerhouse commercial economy, high-volume international convention centers, and rich UNESCO heritage tourism. Sunil Singh implements rigorous, data-driven revenue management strategies engineered to capitalize on trade expo compression, balance weekday corporate demand with weekend staycations, and protect property margins against aggressive comp-set discounting.
          </p>
        </div>

        {/* Seasonality Block */}
        <div className="mb-14 md:mb-18 rounded-lg border border-border bg-card p-6 md:p-8 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-6 border-b border-border/80">
            <div>
              <span className="text-xs font-mono font-semibold uppercase tracking-wider text-secondary">
                Demand Timeline
              </span>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mt-0.5">
                Ahmedabad Hospitality Seasonality &amp; Demand Cycles
              </h3>
            </div>
            <div className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 px-3 py-1.5 rounded-md self-start sm:self-auto">
              <Briefcase className="size-3.5 text-secondary" />
              <span>Commercial &amp; Event-Driven Micro-Market</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
            {seasonalityCycles.map((cycle, idx) => <div key={cycle.phase} data-index={idx} className="flex flex-col justify-between p-4 rounded-md bg-muted/30 border border-border/60">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="font-mono text-sm font-bold text-foreground bg-background px-2.5 py-0.5 rounded-md border border-border/70">
                      {cycle.period}
                    </span>
                    <span className={`text-xs font-semibold ${cycle.badgeColor}`}>
                      Phase 0{idx + 1}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mt-2 mb-1.5">
                    {cycle.phase}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {cycle.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Ahmedabad hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}